import React, { useEffect, useMemo, useState } from 'react';
import {
  Select,
  Radio,
  DatePicker,
  Empty,
  EchartsWithTheme,
  useTheme,
  Form,
  Button,
  message,
} from '@maxtropy/components';
import { Spin, Space, Divider } from 'antd';
import dayjs from 'dayjs';
import { useDevicePoints, useDeviceDetail } from '../../hooks/useDeviceDetail';
import { getDeviceDetailChartOption } from './charts';
import { formatRequestTime, getDefaultDateRange, getMaxRange } from '../../utils';
import { TrendGranularity, type DeviceType } from '../../types';
import { GRANULARITY_OPTIONS } from '../../constants';
import { isEmpty, uniqBy } from 'lodash-es';
import { MeasurementType } from '@/api/history';
import DeviceTab from './DeviceTab';
import { timeFormat } from '../OverallEmissionChart';
import styles from './index.module.scss';
import { V2EnvMonitorConfigGetPostResponse } from '@maxtropy/device-customer-apis-v2';
import qs from 'qs';

const RANGE_MSG_KEY = 'date-range-warning';

interface DeviceDetailProps {
  title: string;
  deviceType: DeviceType;
  devices: V2EnvMonitorConfigGetPostResponse['exhaustDevices'];
  refreshKey: number;
}

const GAS_COLOR = '#00ADFF';
const WATER_COLOR = '#16DD8E';

// 根据 measurementType 获取可用粒度选项（瞬时类型无按月选项）
const getGranularityOptions = (measurementType: MeasurementType) =>
  measurementType === MeasurementType.INSTANT
    ? GRANULARITY_OPTIONS.filter(opt => opt.value !== TrendGranularity.MONTH)
    : GRANULARITY_OPTIONS;

const DeviceDetail: React.FC<DeviceDetailProps> = ({ title, deviceType, devices = [], refreshKey }) => {
  const theme = useTheme();
  const color = deviceType === 'GAS' ? GAS_COLOR : WATER_COLOR;

  const [deviceId, setDeviceId] = useState<number | undefined>(devices?.[0]?.deviceId);
  const [propertyId, setPropertyId] = useState<string>('');
  const [granularity, setGranularity] = useState<TrendGranularity>(TrendGranularity.HOUR);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() =>
    getDefaultDateRange(TrendGranularity.HOUR)
  );

  const { data: points, loading: pointsLoading } = useDevicePoints(deviceId, refreshKey);

  const measurementType = points?.find(p => String(p.id) === propertyId)?.measurementType ?? MeasurementType.INSTANT;
  const granularityOptions = getGranularityOptions(measurementType);
  const datePickerFormat = granularity === TrendGranularity.MONTH ? 'YYYY-MM' : 'YYYY-MM-DD';

  // 设备列表变化时默认选第一个设备
  useEffect(() => {
    if (devices?.length && !deviceId) {
      setDeviceId(devices[0].deviceId);
    }
  }, [devices]);

  // 设备切换后默认选第一个属性，并重置粒度和时间范围
  useEffect(() => {
    const firstPoint = points?.[0];
    if (firstPoint) {
      const opts = getGranularityOptions(firstPoint.measurementType);
      const defaultGran = opts[0].value;
      setPropertyId(String(firstPoint.id));
      setGranularity(defaultGran);
      setDateRange(getDefaultDateRange(defaultGran));
    }
  }, [points]);

  const { data, loading } = useDeviceDetail(
    deviceId!,
    propertyId ? Number(propertyId) : null,
    measurementType,
    granularity,
    dateRange,
    refreshKey
  );

  const chartOption = useMemo(() => {
    if (!data?.chartData?.length) return null;
    return getDeviceDetailChartOption({
      data: data.chartData,
      color,
      unit: data.unit,
      formatStr: timeFormat(granularity),
      upperLimit: data.thresholdLines?.upperLimit,
      lowerLimit: data.thresholdLines?.lowerLimit,
      propertyName: data.propertyName,
    });
  }, [data, granularity, color]);

  const tabData = useMemo(
    () =>
      uniqBy(points ?? [], 'id').map(item => ({
        key: String(item.id),
        name: item.name,
        measurementType: item.measurementType,
      })),
    [points]
  );

  const handleGranularityChange = (gran: TrendGranularity) => {
    setGranularity(gran);
    setDateRange(getDefaultDateRange(gran));
  };

  const handlePropertyChange = (id: string) => {
    const point = points?.find(p => String(p.id) === id);
    const opts = getGranularityOptions(point?.measurementType ?? MeasurementType.INSTANT);
    setPropertyId(id);

    const isCurrentGranularityAvailable = opts.some(opt => opt.value === granularity);
    if (isCurrentGranularityAvailable) return;

    const defaultGran = opts[0].value;
    setGranularity(defaultGran);
    setDateRange(getDefaultDateRange(defaultGran));
  };

  const validateDateRange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      setDateRange(dates);
      return;
    }
    const maxRange = getMaxRange(granularity);
    const diff =
      granularity === TrendGranularity.MONTH ? dates[1].diff(dates[0], 'month') : dates[1].diff(dates[0], 'day');
    if (Math.abs(diff) > maxRange) {
      message.error({
        content: `最多选择 ${maxRange + 1} ${granularity === TrendGranularity.MONTH ? '个月' : '天'}`,
        key: RANGE_MSG_KEY,
      });
      return;
    }
    setDateRange(dates);
  };

  return (
    <div className={styles.container}>
      <Divider variant="dashed" style={{ borderColor: 'rgba(74,144,226,0.5)' }}>
        <div className={styles.title}>{title}</div>
      </Divider>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
        <Form.Item label="设备名称" style={{ marginBottom: 0 }}>
          <Select
            placeholder="选择设备"
            value={deviceId}
            onChange={setDeviceId}
            style={{ width: 200 }}
            options={devices.map(d => ({ label: d.deviceName, value: d.deviceId }))}
          />
        </Form.Item>
      </div>

      {tabData.length > 0 && (
        <DeviceTab
          activeKey={propertyId}
          onTabChange={handlePropertyChange}
          tabData={tabData}
          extral={
            <Button
              type="primary"
              onClick={() => {
                const { startTime, endTime } = formatRequestTime(dateRange!, granularity);
                window.open(
                  `/api/v2/env/monitor/dashboard/export/device-detail` +
                    qs.stringify(
                      {
                        deviceId,
                        propertyId,
                        measurementType,
                        startTime,
                        endTime,
                        granularity,
                        timestamp: new Date().getTime(),
                      },
                      { addQueryPrefix: true }
                    )
                );
              }}
            >
              导出
            </Button>
          }
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Space size={8}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={granularity}
            onChange={e => handleGranularityChange(e.target.value)}
          >
            {granularityOptions.map(opt => (
              <Radio.Button key={opt.value} value={opt.value}>
                {opt.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={dates => validateDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            picker={granularity === TrendGranularity.MONTH ? 'month' : 'date'}
            format={datePickerFormat}
          />
        </Space>
        {data?.propertyId !== 13668 && data?.thresholdLines && data.thresholdLines.upperLimit && (
          <div>
            阈值上限:
            <span style={{ display: 'inline-block', padding: 4, color: '#FFCB47' }}>
              {data?.thresholdLines?.upperLimit ?? '--'}
            </span>
          </div>
        )}
      </div>
      <Spin spinning={loading || pointsLoading}>
        <div style={{ height: 320 }}>
          <EchartsWithTheme
            empty={{ isShowEmpty: isEmpty(chartOption), emptyNode: <Empty style={{ paddingTop: 80 }} /> }}
            option={chartOption}
            theme={theme}
            style={{ height: '100%' }}
          />
        </div>
      </Spin>
    </div>
  );
};

export default DeviceDetail;
